<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfigurableTypeStore } from '../../stores/configurableTypeStore';

const emit = defineEmits<{
    close: [payload: { imported: boolean }];
}>();

const store = useConfigurableTypeStore();
const { t } = useI18n();

const name = ref('');
const file = ref<File | null>(null);
const error = ref<string | null>(null);
const importing = ref(false);

function onFileChange(event: Event) {
    const target = event.target as HTMLInputElement;
    file.value = target.files?.[0] ?? null;
    error.value = null;
}

async function submit() {
    error.value = null;

    if (!name.value.trim()) {
        error.value = t('typeManagement.importDialog.nameRequired');
        return;
    }
    if (!file.value) {
        error.value = t('typeManagement.importDialog.fileRequired');
        return;
    }

    importing.value = true;
    try {
        const result = await store.importType(name.value.trim(), file.value);
        if (result.success) {
            emit('close', { imported: true });
        } else {
            error.value = result.message ?? t('typeManagement.importDialog.importFailed');
        }
    } finally {
        importing.value = false;
    }
}

function cancel() {
    emit('close', { imported: false });
}
</script>

<template>
    <div class="type-import-overlay" @click.self="cancel">
        <div class="type-import-dialog">
            <h3>{{ t('typeManagement.importDialog.title') }}</h3>

            <div class="type-import-field">
                <label>{{ t('typeManagement.importDialog.nameLabel') }}</label>
                <input
                    v-model="name"
                    type="text"
                    :placeholder="t('typeManagement.importDialog.namePlaceholder')"
                    :disabled="importing"
                    @keydown.enter="submit"
                />
            </div>

            <div class="type-import-field">
                <label>{{ t('typeManagement.importDialog.zipLabel') }}</label>
                <input type="file" accept=".zip" :disabled="importing" @change="onFileChange" />
            </div>

            <div v-if="error" class="type-import-error">{{ error }}</div>

            <div class="type-import-actions">
                <button class="btn btn-secondary" :disabled="importing" @click="cancel">
                    {{ t('common.cancel') }}
                </button>
                <button class="btn btn-primary" :disabled="importing" @click="submit">
                    {{ importing ? t('typeManagement.importDialog.importingButton') : t('typeManagement.importDialog.importButton') }}
                </button>
            </div>
        </div>
    </div>
</template>
